import React from 'react';
import { Calendar, ShieldCheck, TrendingUp, RefreshCw } from 'lucide-react';

interface DashboardHeaderProps {
  selectedDate: string;
  onDateChange: (date: string) => void;
  recoveredRevenue: number;
  currencySymbol: string;
  showForecast: boolean;
  onToggleForecast: () => void;
  onRefresh: () => void;
  loading: boolean;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  selectedDate,
  onDateChange,
  recoveredRevenue,
  currencySymbol,
  showForecast,
  onToggleForecast,
  onRefresh,
  loading,
}) => {
  return (
    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-2">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
          Revenue Overview
        </h1>
        <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 font-medium mt-0.5">
          Real-time billing performance, recurring revenue trends, and growth signals.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex items-center gap-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 shadow-xs">
          <Calendar className="w-3.5 h-3.5 text-slate-400" />
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="bg-transparent text-xs font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
          />
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 px-3 py-2 rounded-xl flex items-center gap-2 shadow-xs">
          <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
          <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">
            Recovered: {currencySymbol}{(recoveredRevenue || 0).toLocaleString()}
          </span>
        </div>

        <button
          onClick={onToggleForecast}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-xs ${
            showForecast
              ? 'bg-indigo-600 text-white shadow-indigo-100 hover:bg-indigo-700'
              : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50'
          }`}
        >
          <TrendingUp className="w-3.5 h-3.5" />
          {showForecast ? 'Forecast: On' : 'Forecast: Off'}
        </button>

        <button
          onClick={onRefresh}
          disabled={loading}
          className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-xl transition-all shadow-xs disabled:opacity-50"
          title="Refresh Dashboard Data"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-600' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default DashboardHeader;
